import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { LinkProps } from '@amzn/awsui-components-console/link';

/**
 * Cloudscape `onFollow` handler that keeps an internal link inside the SPA.
 *
 * Works for both Cloudscape `Link` and `Button`: their follow details are the
 * same type — `BaseNavigationDetail`, which is exactly what
 * `LinkProps.FollowDetail` aliases — so one handler serves both with no cast
 * and no per-component duplicate.
 *
 * Cloudscape fires `onFollow` only for an unmodified left click, so Cmd/Ctrl/
 * middle click still open a new tab — which is why the `href` must be kept on
 * the element rather than replaced by this handler.
 *
 * The `href` check is type narrowing, not policy: `BaseNavigationDetail.href`
 * is typed `string | undefined` while `navigate` requires a `string`. A falsy
 * href therefore navigates nowhere and leaves the current URL alone, with the
 * click already swallowed by the `preventDefault` above.
 */
export const useFollowNavigate = (): ((
  event: CustomEvent<LinkProps.FollowDetail>,
) => void) => {
  const navigate = useNavigate();
  return useCallback(
    (event: CustomEvent<LinkProps.FollowDetail>) => {
      event.preventDefault();
      if (event.detail.href) {
        navigate(event.detail.href);
      }
    },
    [navigate],
  );
};
